/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2022-09-26 11:39:10
 *
 *******************************************************/
// @flow
import React, { Component } from 'react';
import _ from 'lodash';
import { withI18n } from '@shopify/react-i18n';
import { connect } from 'react-redux';
import { reduxForm, getFormValues, change, reset } from 'redux-form';
import { Page, Layout, PageActions } from '@shopify/polaris';
import ActionGroup from 'src/components/views/recommendation/manual-recommendation/item/action-group';
import SourceProduct from 'src/components/views/recommendation/manual-recommendation/item/source-product';
import RecommendedProduct from 'src/components/views/recommendation/manual-recommendation/item/recommended-product';
import SelectProductModal from 'src/components/views/recommendation/manual-recommendation/item/modal/select-product-modal';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';
import MainMenu from 'src/components/containers/main-menu';
import * as recommendationAction from 'src/stores/actions/recommendation-actions';

import { routes } from 'src/routes';
import { MANUAL_RECOMMENDATION_TAB_TYPE, NUMBER_PRODUCT } from 'src/constants/recommendation';
import { getShop } from 'src/helpers';
import { confirmLeavePage } from 'src/helpers';
import type { Product } from 'src/models/product';

import type { ComposedProps, State } from './index.type';

class ManualRecommendationEdit extends Component<ComposedProps, State> {
  state = {
    isSave: false,
    isEdit: false,
    isShowSelectSourceProductModal: false,
    isShowSelectRecommendedProductModal: false
  };

  componentDidMount() {
    this.getManualRecommendationById();

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillUnmount() {
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
  }

  render() {
    const {
      i18n,
      sourceProducts,
      recommendedProducts,
      history,
      manualRecommendationLoading,
      manualActionLoading,
      handleSubmit
    } = this.props;
    const { isEdit, isShowSelectRecommendedProductModal, isShowSelectSourceProductModal } =
      this.state;

    const confirmLeave = this.confirmLeave();
    const isDisableSave =
      _.isEmpty(sourceProducts) || _.isEmpty(recommendedProducts) || !confirmLeave;

    const pageTitle = isEdit
      ? i18n.translate('AdminHTML.Recommendation.ManualRecommendation.EditProduct.title')
      : i18n.translate('AdminHTML.Recommendation.ManualRecommendation.AddNew.title');
    return (
      <div className="BC-Page BC-Page__ManualRecommendationNew">
        <MainMenu menuId="menu-recommendation" />
        <Page
          title={pageTitle}
          breadcrumbs={[
            {
              content: i18n.translate(
                'AdminHTML.Recommendation.ManualRecommendation.AddNew.breadcrumb'
              ),
              disabled: manualActionLoading,
              onAction: this.backToManagementPage
            }
          ]}
          primaryAction={
            <div className="BC-Page BC-Page__Button">
              <ActionGroup
                isDisableSave={isDisableSave}
                isLoading={manualActionLoading}
                onSave={handleSubmit(this.handleSave)}
              />
            </div>
          }>
          <Layout>
            <Layout.Section>
              <div className="BC-Container__Notifications">
                {/* Sticky notification for exceeded plan */}
                <ExceededPlanNotification />
              </div>
            </Layout.Section>

            {/* List of source product */}
            <Layout.Section>
              <SourceProduct
                isHiddenAdd={isEdit}
                onToggleModal={() => this.toggleModal('isShowSelectSourceProductModal')}
                sourceProducts={sourceProducts}
                isLoading={manualRecommendationLoading}
                onRemove={this.handleRemoveSourceProduct}
              />
            </Layout.Section>

            {/* List of recommend product */}
            <Layout.Section>
              <RecommendedProduct
                onToggleModal={() => this.toggleModal('isShowSelectRecommendedProductModal')}
                sourceProducts={sourceProducts}
                recommendedProducts={recommendedProducts}
                onRemove={this.handleRemoveRecommendProduct}
                onDragEnd={this.handleDragRecommendProduct}
                isLoading={manualRecommendationLoading}
              />
            </Layout.Section>
          </Layout>

          <SelectProductModal
            active={isShowSelectSourceProductModal}
            toggleModal={() => this.toggleModal('isShowSelectSourceProductModal')}
            title={i18n.translate(
              'AdminHTML.Recommendation.ManualRecommendation.SelectProductModal.sourceProduct_Title'
            )}
            description={i18n.translate(
              'AdminHTML.Recommendation.ManualRecommendation.SelectProductModal.sourceProduct_Description',
              {
                t1: (
                  <span style={{ color: '#916A00', fontWeight: 600 }}>
                    {i18n.translate(
                      'AdminHTML.Recommendation.ManualRecommendation.SelectProductModal.sourceProduct_Description_t1'
                    )}
                  </span>
                )
              }
            )}
            fieldName="sourceProducts"
            selectedProducts={sourceProducts}
            formName="ManualRecommendationForm"
            search={i18n.translate(
              'AdminHTML.Recommendation.ManualRecommendation.SelectProductModal.sourceProduct_Search'
            )}
          />

          <SelectProductModal
            active={isShowSelectRecommendedProductModal}
            toggleModal={() => this.toggleModal('isShowSelectRecommendedProductModal')}
            title={i18n.translate(
              'AdminHTML.Recommendation.ManualRecommendation.SelectProductModal.recommendProduct_Title'
            )}
            description={i18n.translate(
              'AdminHTML.Recommendation.ManualRecommendation.SelectProductModal.recommendProduct_Description',
              {
                t1: (
                  <b>
                    {i18n.translate(
                      'AdminHTML.Recommendation.ManualRecommendation.SelectProductModal.recommendProduct_Description_t1'
                    )}
                  </b>
                ),
                nt: <b>{NUMBER_PRODUCT}</b>
              }
            )}
            limitSelection={NUMBER_PRODUCT}
            fieldName="recommendedProducts"
            selectedProducts={recommendedProducts}
            formName="ManualRecommendationForm"
            search={i18n.translate(
              'AdminHTML.Recommendation.ManualRecommendation.SelectProductModal.recommendProduct_Search'
            )}
          />

          <PageActions
            primaryAction={
              <div className="BC-Page BC-Page__Button">
                <ActionGroup
                  onSave={handleSubmit(this.handleSave)}
                  isLoading={manualActionLoading}
                  isDisableSave={isDisableSave}
                />
              </div>
            }
          />
        </Page>

        {/* Confirm before leaving */}

        <RouteLeavingGuard
          when={confirmLeave}
          // Navigate function
          navigate={path => history.push(path)}
          shouldBlockNavigation={confirmLeave}
          isAvailableFullscreen={true}
        />
      </div>
    );
  }

  confirmLeave = () => {
    const { isEdit, isSave } = this.state;

    const { sourceProducts, recommendedProducts, manualRecommendation } = this.props;

    // when save button click => allow leave
    if (isSave) return false;

    // On create
    // if sourceProduct more than 0 => confirm before leave
    if (!isEdit) return sourceProducts.length > 0;

    // On Edit
    // manualRecommendation => confirm before leave
    if (manualRecommendation === null) return true;

    const originRecommendedProducts = _.get(manualRecommendation, 'data.recommendedProducts', []);

    // if length of origin recommendedProducts not equal length of current recommendedProducts
    // => confirm before leave
    if (recommendedProducts.length !== originRecommendedProducts.length) return true;

    // if change position of product in list => confirm before leave
    for (let index = 0; index < recommendedProducts.length; index++) {
      const originProduct = originRecommendedProducts[index];
      const product = recommendedProducts[index];

      if (originProduct.id !== product.id) return true;
    }

    // allow leave
    return false;
  };

  getManualRecommendationById = async () => {
    const { getManualRecommendationById, updateManualRecommendationForm } = this.props;
    const { recommendationId } = this.props.match.params;

    if (recommendationId) {
      this.setState({
        isEdit: true
      });
      const result = await getManualRecommendationById(recommendationId);
      const { data } = result?.payload;
      updateManualRecommendationForm(
        'sourceProducts',
        data?.mainProduct ? [data?.mainProduct] : []
      );
      updateManualRecommendationForm('recommendedProducts', data?.recommendedProducts || []);
    }
  };

  // Handle back to manual recommendation dashboard
  backToManagementPage = () => {
    const { history } = this.props;
    history.push(routes.manualRecommendation.list());
  };

  handleSave = async data => {
    const {
      createManualRecommendation,
      editManualRecommendation,
      resetForm,
      history,
      manualRecommendation
    } = this.props;
    const { sourceProducts, recommendedProducts } = data;
    const { recommendationId } = this.props.match.params;

    const mainProducts = sourceProducts.map(item => item.id);
    const recommendedProductIds = recommendedProducts.map(item => item.id);

    // Edit manual recommendation
    if (recommendationId) {
      const tenantId = getShop();

      const result = await editManualRecommendation(recommendationId, {
        id: recommendationId,
        tenantId,
        recommendationType: manualRecommendation?.recommendationType,
        mainProduct: mainProducts[0],
        recommendedProducts: recommendedProductIds
      });
      if (!_.isEmpty(result?.payload?.data)) {
        this.setState({ isSave: true });
        resetForm();
        history.push(routes.manualRecommendation.list());
      }
    } else {
      // Add manual recommendation
      const result = await createManualRecommendation({
        recommendationType: MANUAL_RECOMMENDATION_TAB_TYPE.HAND_PICKED_PRODUCTS, // TODO: Consider to improve dynamic type or not
        mainProducts,
        recommendedProducts: recommendedProductIds
      });
      if (!_.isEmpty(result?.payload?.data?.data)) {
        this.setState({ isSave: true });
        resetForm();
        history.push(routes.manualRecommendation.list());
      }
    }
  };

  // Remove source product from redux-form field
  handleRemoveSourceProduct = (product: Product) => {
    const { sourceProducts, updateManualRecommendationForm } = this.props;
    const newSourceProducts = _.filter(sourceProducts, item => item.id !== product.id);
    updateManualRecommendationForm('sourceProducts', newSourceProducts);
    if (_.isEmpty(newSourceProducts)) {
      updateManualRecommendationForm('recommendedProducts', []);
    }
  };

  // Remove recommended product from redux-form field
  handleRemoveRecommendProduct = (product: Product) => {
    const { recommendedProducts, updateManualRecommendationForm } = this.props;
    const newRecommendedProducts = _.filter(recommendedProducts, item => item.id !== product.id);
    updateManualRecommendationForm('recommendedProducts', newRecommendedProducts);
  };

  // Update recommended products after reordering
  handleDragRecommendProduct = (product: Product[]) => {
    const { updateManualRecommendationForm } = this.props;
    updateManualRecommendationForm('recommendedProducts', product);
  };

  toggleModal = (name: string) => {
    this.setState({ [name]: !this.state[name] });
  };

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { i18n } = this.props;
    return confirmLeavePage(event, this.confirmLeave(), i18n);
  };
}

const ManualRecommendationForm = reduxForm({
  form: 'ManualRecommendationForm', // a unique identifier for this form
  enableReinitialize: true
})(ManualRecommendationEdit);

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Update edit form
  updateManualRecommendationForm: (field, value) =>
    dispatch(change(`ManualRecommendationForm`, field, value)),

  resetForm: () => dispatch(reset('ManualRecommendationForm')),

  // Get manual recommendation by id
  getManualRecommendationById: recommendationId =>
    dispatch(recommendationAction.getManualRecommendationById(recommendationId, ownProps.i18n)),

  // Create manual recommendations
  createManualRecommendation: data =>
    dispatch(recommendationAction.createManualRecommendation(data, ownProps.i18n)),

  //Edit manual recommendation
  editManualRecommendation: (recommendationId, data) =>
    dispatch(recommendationAction.editManualRecommendation(recommendationId, data, ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  const formValues = getFormValues('ManualRecommendationForm')(state);
  const sourceProducts = _.get(formValues, 'sourceProducts', []);
  const recommendedProducts = _.get(formValues, 'recommendedProducts', []);

  // Get manual recommendation
  const manualRecommendation = _.get(state.recommendation, 'manualRecommendation', null);
  const manualRecommendationLoading = _.get(
    state.recommendation,
    'manualRecommendationLoading',
    false
  );
  const manualActionLoading = _.get(state.recommendation, 'manualActionLoading', false);

  return {
    sourceProducts,
    recommendedProducts,
    manualRecommendation,
    manualRecommendationLoading,
    manualActionLoading
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(ManualRecommendationForm));
